import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import {
  DOCUMENT_TYPE_LABEL,
  DOCUMENT_TYPE_LABEL_KEY,
  SUBMISSION_STATUS_LABEL,
  SUBMISSION_STATUS_LABEL_KEY,
  useFiscalSubmissions,
  type FiscalSubmissionStatus,
} from '@/hooks/use-fiscal-cabinet';
import { useT } from '@/i18n/use-t';
import { EmptyPanel, FiscalCabinetSection, Pager } from './fiscal-cabinet-section';

const PAGE_SIZE = 50;

function statusVariant(status: FiscalSubmissionStatus) {
  switch (status) {
    case 'kvt2':
      return 'success' as const;
    case 'rejected':
      return 'destructive' as const;
    case 'draft':
      return 'outline' as const;
    default:
      return 'default' as const;
  }
}

function formatPeriod(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, '0')}`;
}

function SubmissionsList({ available }: { available: boolean }) {
  const t = useT('admin_ui');
  const [page, setPage] = useState(1);
  const { data, isLoading, isError } = useFiscalSubmissions(
    { page, pageSize: PAGE_SIZE },
    available,
  );

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">{t('common.loading', 'Loading…')}</p>;
  }
  if (isError || data === undefined) {
    return (
      <p className="text-sm text-destructive">
        {t('fiscalCabinet.loadError', 'Failed to load the fiscal cabinet.')}
      </p>
    );
  }
  if (data.items.length === 0) {
    return (
      <EmptyPanel>
        {t(
          'fiscalCabinet.submissions.empty',
          'No submissions yet. Store, PRRO and cashier registrations appear here once they are sent to the tax service.',
        )}
      </EmptyPanel>
    );
  }

  return (
    <div className="space-y-3">
      <div className="rounded-md border border-border bg-card">
        <Table>
          <THead>
            <TR>
              <TH>{t('fiscalCabinet.submissions.col.created', 'Created')}</TH>
              <TH>{t('fiscalCabinet.submissions.col.document', 'Document')}</TH>
              <TH>{t('fiscalCabinet.submissions.col.tin', 'Tax ID')}</TH>
              <TH>{t('fiscalCabinet.submissions.col.form', 'Form')}</TH>
              <TH className="text-right">{t('fiscalCabinet.submissions.col.counter', 'No.')}</TH>
              <TH>{t('fiscalCabinet.submissions.col.period', 'Period')}</TH>
              <TH>{t('fiscalCabinet.submissions.col.status', 'Status')}</TH>
              <TH>{t('fiscalCabinet.submissions.col.dpsId', 'Tax service ID')}</TH>
            </TR>
          </THead>
          <TBody>
            {data.items.map((s) => (
              <TR key={s.id}>
                <TD className="whitespace-nowrap font-mono text-xs">
                  {s.createdAt.slice(0, 16).replace('T', ' ')}
                </TD>
                <TD>
                  {t(DOCUMENT_TYPE_LABEL_KEY[s.documentType], DOCUMENT_TYPE_LABEL[s.documentType])}
                </TD>
                <TD className="font-mono text-xs">{s.tin}</TD>
                <TD className="font-mono text-xs">{s.formCode}</TD>
                <TD className="text-right font-mono text-xs">{s.documentCounter}</TD>
                <TD className="font-mono text-xs">{formatPeriod(s.periodYear, s.periodMonth)}</TD>
                <TD>
                  <Badge variant={statusVariant(s.status)}>
                    {t(SUBMISSION_STATUS_LABEL_KEY[s.status], SUBMISSION_STATUS_LABEL[s.status])}
                  </Badge>
                </TD>
                <TD className="font-mono text-xs">{s.dpsDocumentId ?? '—'}</TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </div>
      <Pager page={page} pageSize={PAGE_SIZE} total={data.total} onPage={setPage} />
    </div>
  );
}

/** PFC-9 — Fiscal cabinet → Submissions. */
export function FiscalSubmissionsPage() {
  return (
    <FiscalCabinetSection>
      {(available) => <SubmissionsList available={available} />}
    </FiscalCabinetSection>
  );
}
